import React, { Component } from "react";
import { AppCard, AppBadge, PageLayout } from "../../components/ui";
import _ from "lodash";
import { Column } from "primereact/column";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { numberFilterMatchModeOptions, stringFilterMatchModeOptions, dateFilterTemplate } from "../Paginator";
import { kycSubmissionApiService } from "../../services/api/Kyc/KycSubmissionApiService";
import { KycSubmission } from "../../models/KycSubmission";
import { Button } from "primereact/button";
import { SelectItem } from "../../models/SelectItem";
import { fileService } from "../../services/FileService";
import { saveAs } from "file-saver";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { utils } from "../../utils/utils";
import JSZip from "jszip";
import { Download } from "lucide-react";
import { toastService } from "../../services/toastService";
interface PendingKycSubmissonsState {
    isLoading: boolean;
    submissions: KycSubmission[];
    viewKycSubmissionModal: boolean;
    selectedSubmission: KycSubmission | null;
    kycStatusTypes: SelectItem<number>[];
    totalRecords: number;
}
export default class ClosedKycSubmissions extends Component<any, PendingKycSubmissonsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: any | Readonly<any>) {
        super(props);

        this.state = {
            isLoading: false,
            submissions: [],
            viewKycSubmissionModal: false,
            selectedSubmission: null,
            kycStatusTypes: [],
            totalRecords: 0,
        };
    }

    loadSubmissionCompletedData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        const submissionResult = await kycSubmissionApiService.getAllCompleted(searchRequest);

        this.setState({ isLoading: false });

        if (!submissionResult.success) {
            toastService.error(submissionResult.message);
            return;
        }

        this.setState({ submissions: submissionResult.data, totalRecords: submissionResult.totalCount });
    };

    async componentDidMount() {
        this.setState({ isLoading: true });

        const kycStatusTypesResult = await kycSubmissionApiService.getKycStatusTypes();

        if (!kycStatusTypesResult.success) {
            toastService.error(kycStatusTypesResult.message);
            return;
        }

        this.setState({ kycStatusTypes: kycStatusTypesResult.data });
    }

    onActionButtonClick = (row: KycSubmission) => {
        this.setState({ viewKycSubmissionModal: true, selectedSubmission: row });
    };

    actionBodyTemplate = (row: KycSubmission) => {
        return <Button label="Review" type="button" severity="warning" onClick={() => this.onActionButtonClick(row)}></Button>;
    };

    renderStatusBadge(status: number) {
        const kycStatus = this.state.kycStatusTypes.filter((f) => f.value == status)[0];

        if (kycStatus == null || kycStatus == undefined) {
            console.error(`No Status found for status=${status}`);
            return;
        }

        const title = kycStatus.label;
        const className = title == "Approved" ? "success" : "danger";

        return <AppBadge variant={className as any}>{title}</AppBadge>;
    }

    downloadDocuments = async (submissionId: number) => {
        this.setState({ isLoading: true });

        const documentsResult = await kycSubmissionApiService.getDocuments(submissionId);

        if (!documentsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(documentsResult.message);
            return;
        }

        const zip = new JSZip();
        const photoZip = zip.folder("documents");

        const data = documentsResult.data;
        for (let i = 0; i < data.length; i++) {
            const result = data[i];
            await this.onDownloadFromFileStorage(result.fileStorageId, result.fileName, photoZip);
        }
        /// waits for "await DownloadFileFromS3, then executes the save as which saves the zipped folder created in "DownloadFileFromS3"
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "documents");
        this.setState({ isLoading: false });
    };

    onDownloadFromFileStorage = async (fileStorageId: string, secondaryLabel: string, photoZip: any): Promise<void> => {
        const imagePath = await fileService.GetFile(fileStorageId);

        if (!imagePath.success) {
            this.setState({ isLoading: false });
            toastService.error(imagePath.message);
            return;
        }

        photoZip.file(secondaryLabel, imagePath.data, { base64: true });

        this.setState({ isLoading: false });
    };

    render() {
        return (
            <PageLayout noTitle>
                                <AppCard className="!mt-0">
                                    <ExtendedDataTable
                                        value={this.state.submissions}
                                        totalRecords={this.state.totalRecords}
                                        onChange={this.loadSubmissionCompletedData}
                                        loading={this.state.isLoading}
                                        ref={(ref) => (this.dataTable = ref)}
                                        defaultSortOrder={SortOrderType.Descending}
                                        defaultSortField="submissionTimeStamp"
                                    >
                                        <Column {...({ alwaysVisible: true } as any)} field="id" header="Submission ID" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                                        <Column
                                        {...({ alwaysVisible: true } as any)}
                                            header="End User First Name"
                                            field="endUserFirstName"
                                            body={(rowData: KycSubmission) => <span>{rowData.endUserFirstName}</span>}
                                            filterMatchModeOptions={stringFilterMatchModeOptions}
                                        ></Column>
                                        <Column
                                            field="merchantName"
                                            header="Merchant Name"
                                            sortable
                                            {...({ alwaysVisible: true } as any)}
                                            filterMatchModeOptions={stringFilterMatchModeOptions}
                                        ></Column>

                                        <Column
                                            header="End User Last Name"
                                            field="endUserLastName"
                                            body={(rowData: KycSubmission) => <span>{rowData.endUserLastName}</span>}
                                            filterMatchModeOptions={stringFilterMatchModeOptions}
                                        ></Column>
                                        <Column
                                            field="endUserPhone"
                                            header="End User Phone"
                                            sortable
                                            filterMatchModeOptions={stringFilterMatchModeOptions}
                                        ></Column>
                                        <Column
                                            field="endUserEmail"
                                            header="End User Email"
                                            sortable
                                            filterMatchModeOptions={stringFilterMatchModeOptions}
                                        ></Column>
                                        <Column
                                            field="submissionTimeStamp"
                                            header="Submission TimeStamp"
                                            body={dateBodyTemplate("submissionTimeStamp")}
                                            dataType="date"
                                            filterElement={dateFilterTemplate}
                                            filterMatchModeOptions={numberFilterMatchModeOptions}
                                        ></Column>
                                        <Column
                                            body={(rowData: KycSubmission) => (
                                                <button
                                                    type="button"
                                                    disabled={rowData.documentsCount === 0}
                                                    onClick={(e) => { e.stopPropagation(); this.downloadDocuments(rowData.id); }}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                                    title="Download documents"
                                                >
                                                    <Download size={14} />
                                                    <span>Download</span>
                                                </button>
                                            )}
                                            header="Uploaded Files"
                                        ></Column>
                                        <Column field="reviewerFirstName" header="KYC Officer"></Column>
                                        <Column field="reason" header="Reason" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                                        <Column
                                            field="reviewStartedTimeStamp"
                                            header="Review Started"
                                            body={dateBodyTemplate("reviewStartedTimeStamp")}
                                            filterMatchModeOptions={numberFilterMatchModeOptions}
                                            dataType="date"
                                            filterElement={dateFilterTemplate}
                                        ></Column>
                                        <Column
                                            field="reviewCompletedTimeStamp"
                                            header="Review Closed"
                                            sortField=""
                                            body={dateBodyTemplate("reviewCompletedTimeStamp")}
                                            filterMatchModeOptions={numberFilterMatchModeOptions}
                                            dataType="date"
                                            filterElement={dateFilterTemplate}
                                        ></Column>
                                        <Column
                                            field="status"
                                            header="Status"
                                            body={(rowData: KycSubmission) => this.renderStatusBadge(rowData.status)}
                                        ></Column>
                                    </ExtendedDataTable>
                                </AppCard>
            </PageLayout>
        );
    }
}
